import React, { useState, useEffect, useCallback } from 'react';
import { getDashboard, envoyerRappelEmail } from '../api';
import StatusBadge from './StatusBadge';

function fmt(n) {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n || 0);
}

function moisLabel(dateISO) {
  return new Date(dateISO).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const [sendingRappel, setSendingRappel] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await getDashboard();
      setData(res.data);
    } catch {
      setAlert({ type: 'error', message: 'Erreur lors du chargement du tableau de bord' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const showAlert = (message, type = 'success') => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 4000);
  };

  const handleRappel = async (facture) => {
    if (!facture.client_email) {
      showAlert('Aucun email pour ce client', 'error');
      return;
    }
    setSendingRappel(facture.id);
    try {
      await envoyerRappelEmail(facture.id);
      showAlert(`Rappel envoyé à ${facture.client_email}`);
    } catch (err) {
      showAlert(err.response?.data?.error || 'Erreur envoi rappel', 'error');
    } finally {
      setSendingRappel(null);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-slate-500)' }}>
        Chargement…
      </div>
    );
  }

  if (!data) return null;

  const tauxRecouvrement = data.ca_mois.ttc > 0
    ? Math.round((data.encaissements_mois / data.ca_mois.ttc) * 100)
    : 0;

  return (
    <div style={{ padding: '0 0 48px' }}>
      {alert && (
        <div style={{
          position: 'fixed', top: 16, right: 16, zIndex: 1000,
          background: alert.type === 'error' ? 'var(--color-red-50)' : 'var(--color-green-50)',
          color: alert.type === 'error' ? 'var(--color-red-700)' : 'var(--color-green-700)',
          border: `1px solid ${alert.type === 'error' ? 'var(--color-red-200)' : 'var(--color-green-200)'}`,
          borderRadius: 'var(--radius-lg)', padding: '12px 20px', fontSize: 14,
          boxShadow: 'var(--shadow-md)',
        }}>
          {alert.message}
        </div>
      )}

      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--color-slate-900)', margin: '0 0 4px' }}>
          Tableau de bord
        </h1>
        <p style={{ color: 'var(--color-slate-500)', fontSize: 14, margin: 0 }}>
          {moisLabel(data.periode.debut)}
        </p>
      </div>

      {/* KPI Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 40 }}>
        <KpiCard
          label="CA facturé (mois)"
          value={fmt(data.ca_mois.ttc)}
          sub={`${data.ca_mois.nb_factures} facture${data.ca_mois.nb_factures > 1 ? 's' : ''} · HT ${fmt(data.ca_mois.ht)}`}
          color="var(--color-blue-600)"
        />
        <KpiCard
          label="Encaissé (mois)"
          value={fmt(data.encaissements_mois)}
          sub={`${tauxRecouvrement} % du CA facturé`}
          color="var(--color-green-600)"
        />
        <KpiCard
          label="Impayés total"
          value={fmt(data.total_impayes)}
          sub={`${data.impayes.length} facture${data.impayes.length > 1 ? 's' : ''} en attente`}
          color={data.total_impayes > 0 ? 'var(--color-amber-600)' : 'var(--color-green-600)'}
        />
        <KpiCard
          label="TVA collectée (mois)"
          value={fmt(data.tva_collectee_mois)}
          sub="À déclarer sur la période"
          color="var(--color-slate-600)"
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        {/* Impayés */}
        <section>
          <h2 style={{ fontSize: 16, fontWeight: 600, color: 'var(--color-slate-800)', margin: '0 0 12px' }}>
            Factures impayées
          </h2>
          {data.impayes.length === 0 ? (
            <div style={{
              background: 'var(--color-green-50)', border: '1px solid var(--color-green-200)',
              borderRadius: 'var(--radius-lg)', padding: '20px 16px',
              color: 'var(--color-green-700)', fontSize: 14, textAlign: 'center',
            }}>
              Aucun impayé — félicitations !
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {data.impayes.map((f) => (
                <div key={f.id} style={{
                  background: '#fff', border: '1px solid var(--color-slate-200)',
                  borderRadius: 'var(--radius-lg)', padding: '12px 16px',
                  borderLeft: f.jours_retard > 0 ? '3px solid var(--color-red-400)' : '3px solid var(--color-amber-400)',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--color-slate-900)' }}>
                          {f.numero}
                        </span>
                        <StatusBadge statut={f.statut} type="facture" />
                        {f.jours_retard > 0 && (
                          <span style={{
                            background: 'var(--color-red-50)', color: 'var(--color-red-600)',
                            border: '1px solid var(--color-red-200)',
                            borderRadius: 'var(--radius-full)', padding: '1px 8px', fontSize: 11, fontWeight: 600,
                          }}>
                            {f.jours_retard}j de retard
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--color-slate-500)', marginTop: 3 }}>
                        {f.client_nom}
                        {f.date_echeance && ` · échéance ${new Date(f.date_echeance).toLocaleDateString('fr-FR')}`}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--color-slate-900)' }}>
                        {fmt(f.montant_restant)}
                      </div>
                      {f.montant_paye > 0 && (
                        <div style={{ fontSize: 11, color: 'var(--color-slate-400)' }}>
                          encaissé {fmt(f.montant_paye)}
                        </div>
                      )}
                      {f.client_email && (
                        <button
                          onClick={() => handleRappel(f)}
                          disabled={sendingRappel === f.id}
                          style={{
                            marginTop: 6, fontSize: 11, padding: '3px 10px',
                            background: 'var(--color-blue-600)', color: '#fff',
                            border: 'none', borderRadius: 'var(--radius-md)',
                            cursor: sendingRappel === f.id ? 'wait' : 'pointer',
                            opacity: sendingRappel === f.id ? 0.6 : 1,
                          }}
                        >
                          {sendingRappel === f.id ? 'Envoi…' : 'Rappel'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Top clients */}
        <section>
          <h2 style={{ fontSize: 16, fontWeight: 600, color: 'var(--color-slate-800)', margin: '0 0 12px' }}>
            Top clients
          </h2>
          {data.top_clients.filter(c => c.ca_total > 0).length === 0 ? (
            <div style={{
              background: 'var(--color-slate-50)', border: '1px solid var(--color-slate-200)',
              borderRadius: 'var(--radius-lg)', padding: '20px 16px',
              color: 'var(--color-slate-500)', fontSize: 14, textAlign: 'center',
            }}>
              Aucune facture émise pour le moment
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {data.top_clients.filter(c => c.ca_total > 0).map((client, i) => {
                const maxCA = data.top_clients[0]?.ca_total || 1;
                const pct = Math.round((client.ca_total / maxCA) * 100);
                return (
                  <div key={client.id} style={{
                    background: '#fff', border: '1px solid var(--color-slate-200)',
                    borderRadius: 'var(--radius-lg)', padding: '10px 16px',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{
                          width: 20, height: 20, borderRadius: '50%',
                          background: i === 0 ? 'var(--color-blue-600)' : 'var(--color-slate-200)',
                          color: i === 0 ? '#fff' : 'var(--color-slate-600)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 10, fontWeight: 700, flexShrink: 0,
                        }}>
                          {i + 1}
                        </span>
                        <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--color-slate-900)' }}>
                          {client.nom}
                        </span>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--color-slate-900)' }}>
                          {fmt(client.ca_total)}
                        </span>
                        <span style={{ fontSize: 11, color: 'var(--color-slate-400)', marginLeft: 6 }}>
                          {client.nb_factures} fact.
                        </span>
                      </div>
                    </div>
                    <div style={{ height: 4, background: 'var(--color-slate-100)', borderRadius: 2 }}>
                      <div style={{
                        height: '100%', width: `${pct}%`,
                        background: i === 0 ? 'var(--color-blue-600)' : 'var(--color-slate-300)',
                        borderRadius: 2, transition: 'width 0.4s ease',
                      }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function KpiCard({ label, value, sub, color }) {
  return (
    <div style={{
      background: '#fff', border: '1px solid var(--color-slate-200)',
      borderRadius: 'var(--radius-lg)', padding: '20px 20px 16px',
      borderTop: `3px solid ${color}`,
    }}>
      <div style={{ fontSize: 12, color: 'var(--color-slate-500)', fontWeight: 500, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </div>
      <div style={{ fontSize: 26, fontWeight: 700, color: 'var(--color-slate-900)', marginBottom: 4 }}>
        {value}
      </div>
      <div style={{ fontSize: 12, color: 'var(--color-slate-400)' }}>
        {sub}
      </div>
    </div>
  );
}
